/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { Select } from '../Select'
import { ErrorMessageStyled } from '../shared/ErrorMessage'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { useEsignature } from '../../hooks/useEsignature'
import { transformToSelectOptions } from '../../utils/formHelpers'

/**
 * Set Owner Modal Component
 *
 * Allows users to set/change the owner of selected file system items (FSIs).
 * Supports e-signature validation for file commands if configured.
 *
 * Features:
 * - Single or multiple FSI owner assignment
 * - User selection from available users list
 * - E-signature validation support
 * - Integration with file update events
 *
 * Migrated from: lego/page/browser/SetOwner.jsx
 */

interface SetOwnerModalProps {
  options: {
    selectedIds: number[]
    [key: string]: unknown
  }
  onClose?: () => void
}

interface SelectOption {
  value: number | null
  label: string
}

interface FormValues {
  owner: number | null
}

const validationSchema = Yup.object({
  owner: Yup.number().nullable().required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
})

export const SetOwnerModal: React.FC<SetOwnerModalProps> = ({ options, onClose }) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { api, $rootScope } = context
  const { signAction } = useEsignature()
  const { hideModal } = useModal()
  const [isOpen, setIsOpen] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<SelectOption[]>([])
  const defaultFilterConst: FormValues = { owner: null }

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
    hideModal()
  }

  const loadData = () => {
    api.users.getAssembledUsersGroups().then(function (data) {
      setAvailableUsers(transformToSelectOptions(data.users, 'id', 'name') as SelectOption[])
    })
  }

  const save = async (values: FormValues) => {
    doClose()

    const doSetOwnerCallback = async function (validation?: Record<string, unknown>) {
      try {
        await api.files.setFilesOwner(options.selectedIds, values.owner as number, validation, options)

        // Emit global events for backward compatibility (window event bus)
        window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateDashboard', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateSearchFiles'))
        toast.success(translate('BROWSER_SETTINGS_SET_OWNER_SUCCESS_MSG'))
      } catch (error: any) {
        toast.error(translate('ERROR_ERROR', { error: error?.data?.message || error?.message }))
      }
    }

    if (auth.requireESignatureForFileCommands()) {
      try {
        const validation = await signAction('ADMIN_ACTION', translate('ADMIN_ACTION_SET_FSI_OWNER'))
        return doSetOwnerCallback(validation as Record<string, unknown>)
      } catch (error) {
        // E-signature was cancelled or failed
        return
      }
    } else {
      return doSetOwnerCallback()
    }
  }

  return (
    <>
      <Formik
        validate={(values) => {
          const errors: Partial<Record<keyof FormValues, string>> = {}
          if (Object.is(values?.owner, null)) {
            errors.owner = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          return errors
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
        innerRef={(el) => {
          if (el && context.store) {
            context.store.formRef = el
          }
        }}
        initialValues={defaultFilterConst}
        onSubmit={(values) => {
          save(values)
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <div>
            <Modal show={isOpen} handleClose={doClose} title={translate('BROWSER_SETTINGS_SET_OWNER_H_TITLE')}>
              <Modal.Body>
                <div>
                  <div className="form-group md-form-group" id="6a74f16d-7f2b-4a85-81f7-3bd35723f00c">
                    <label className="control-label">{translate('BROWSER_SETTINGS_SET_OWNER_LABEL')}</label>
                    <Select
                      placeholder={translate('SELECT_USER...')}
                      name="owner"
                      value={values.owner}
                      options={availableUsers}
                      onChange={(option: any) => {
                        const selectedOption = option as SelectOption | null
                        setFieldValue('owner', selectedOption?.value ? selectedOption.value : null)
                      }}
                      multi={false}
                      showNewOptionAtTop={true}
                      searchable
                      id="6a74f16d-7f2b-4a85-81f7-3bd35723f00c"
                    />
                    <ErrorMessageStyled name="owner" classesName="fs14" />
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button
                  data-action="ok"
                  className="btn btn-link"
                  onClick={doClose}
                  id="b2a1338b-8980-4219-b33b-68063f5bcfa3"
                  type="button"
                >
                  {translate('CANCEL')}
                </button>
                <button
                  data-action="ok"
                  className="btn btn-primary"
                  onClick={() => handleSubmit()}
                  id="565f2722-7825-46ec-ab1a-a7dded5f6846"
                  type="button"
                >
                  {translate('OK')}
                </button>
              </Modal.Footer>
            </Modal>
          </div>
        )}
      </Formik>
    </>
  )
}

export default SetOwnerModal
